import { apiFetch, readErrorMessage, readJson } from './client';

export interface AsciiArtItem {
  id: string;
  externalId: string;
  name: string;
  art: string;
  category: string;
  subcategory: string | null;
  tags: string[];
  lineCount: number;
  isActive: boolean;
  origin: string;
}

export interface AsciiArtPage {
  items: AsciiArtItem[];
  total: number;
  skip: number;
  take: number;
}

export interface AsciiArtCategory {
  name: string;
  count: number;
}

export interface AsciiArtQuery {
  q?: string;
  category?: string;
  subcategory?: string;
  skip?: number;
  take?: number;
}

export interface AsciiArtUpsertRequest {
  name: string;
  art: string;
  category: string;
  subcategory?: string | null;
  tags?: string[];
  aliases?: string[];
  isActive: boolean;
}

function toQueryString(query: AsciiArtQuery): string {
  const parts: string[] = [];
  if (query.q) parts.push(`q=${encodeURIComponent(query.q)}`);
  if (query.category) parts.push(`category=${encodeURIComponent(query.category)}`);
  if (query.subcategory) parts.push(`subcategory=${encodeURIComponent(query.subcategory)}`);
  if (query.skip != null) parts.push(`skip=${encodeURIComponent(String(query.skip))}`);
  if (query.take != null) parts.push(`take=${encodeURIComponent(String(query.take))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

export async function listAsciiArt(query: AsciiArtQuery = {}): Promise<AsciiArtPage> {
  const res = await apiFetch(`/api/ascii-art${toQueryString(query)}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  const raw = await readJson<Partial<AsciiArtPage>>(res);
  return {
    items: raw.items ?? [],
    total: Number(raw.total ?? 0),
    skip: Number(raw.skip ?? 0),
    take: Number(raw.take ?? 0)
  };
}

export async function listAsciiArtCategories(): Promise<AsciiArtCategory[]> {
  const res = await apiFetch('/api/ascii-art/categories');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<AsciiArtCategory[]>(res);
}

export async function listAsciiArtAdmin(query: AsciiArtQuery = {}): Promise<AsciiArtPage> {
  const res = await apiFetch(`/api/ascii-art/admin/items${toQueryString(query)}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  const raw = await readJson<Partial<AsciiArtPage>>(res);
  return {
    items: raw.items ?? [],
    total: Number(raw.total ?? 0),
    skip: Number(raw.skip ?? 0),
    take: Number(raw.take ?? 0)
  };
}

export async function createAsciiArtItem(body: AsciiArtUpsertRequest): Promise<AsciiArtItem> {
  const res = await apiFetch('/api/ascii-art/admin/items', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<AsciiArtItem>(res);
}

export async function updateAsciiArtItem(
  id: string,
  body: AsciiArtUpsertRequest
): Promise<AsciiArtItem> {
  const res = await apiFetch(`/api/ascii-art/admin/items/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<AsciiArtItem>(res);
}

export async function deactivateAsciiArtItem(id: string): Promise<void> {
  const res = await apiFetch(`/api/ascii-art/admin/items/${encodeURIComponent(id)}/deactivate`, {
    method: 'POST'
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
}
